import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Trophy, Users, ArrowRight, Plus, WifiOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useUserLeagues } from "../hooks/useUserLeagues";
import CfbNewsBanner from "../components/CfbNewsBanner";
import ThisWeekBanner from "../components/ThisWeekBanner";
import SeasonCountdown from "../components/SeasonCountdown";
import WhatsNewCard from "../components/WhatsNewCard";
import WeeklyAwardsCard from "../components/WeeklyAwardsCard";
import TopTeamsCard from "../components/TopTeamsCard";
import GamesSection from "../components/GamesSection";
import WeeklyGameSection from "../components/WeeklyGameSection";
import LeagueRail from "../components/LeagueRail";
import HelpMark from "../components/HelpMark";
import { SECTION_HEADING } from "../utils/homeType";
import HomeNavBar from "../components/nav/HomeNavBar";
import DemoBar from "../components/nav/DemoBar";
import { usePageTour } from "../hooks/usePageTour";

// demo: /demo, the signed-out look around linked from the landing page. The
// same page with the public demo league as the visitor's league, and Sign up
// where a member would create or join one.
// The steps the "?" beside Your Leagues walks through, in the order they sit
// on the card. Copy lives in tours/tourSteps.js with the rest of the tour.
const LEAGUE_CARD_STEPS = ["home-league-news", "home-league-lineup", "home-league-table"];

function Home({ demo = false }) {
  // --- Session data from AuthContext (no extra DB queries needed) ---
  const { userData } = useAuth();
  const isAdmin = userData?.is_admin || false;
  const userName = userData?.first_name || "";

  // --- League list (shared with the nav bar's league switcher sheet) ---
  const { leagues: leagueList, loading: leaguesLoading } = useUserLeagues({ demo, live: true });
  // In the guided demo tour, Home hands on to the demo league's lineup.
  const demoLeagueId = demo ? leagueList[0]?.id : null;
  usePageTour("home", {
    demo,
    nextLabel: "Next: your lineup",
    nextTo: demoLeagueId ? `/${demoLeagueId}/lineup` : null,
  });

  // --- Local state ---
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // --- Routing ---
  const navigate = useNavigate();
  const location = useLocation();
  const flashMessage = location.state?.message; // optional success banner passed via navigate(..., { state: { message } })

  // --- Effect 1: Online/offline detection ---
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // --- Effect 2: Redirect admin users immediately ---
  useEffect(() => {
    if (isAdmin) navigate("/admin");
  }, [isAdmin, navigate]);

  // --- Early returns ---

  // Spinner shown while league list is loading
  if (leaguesLoading) {
    return (
      <div className="theme-auto" style={{ minHeight: '100vh', backgroundColor: 'var(--color-bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', border: '2.5px solid var(--color-brand)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // --- Render ---
  return (
    /* theme-auto is what opts this route into dark mode. The tokens it
       redefines are declared light at :root, so every page that has not been
       migrated keeps resolving the light values and is untouched. */
    <div className="theme-auto" style={{ minHeight: '100vh', backgroundColor: 'var(--color-bg-page)', paddingBottom: demo ? 120 : 80 }}>

      {/* Offline banner — shown when browser reports no network connection */}
      {!isOnline && (
        <div style={{ backgroundColor: 'var(--color-danger-bg)', borderBottom: '1px solid var(--color-danger-border)', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <WifiOff size={14} color="var(--color-danger-text)" />
          <span style={{ fontSize: 13, color: 'var(--color-danger-text)', fontWeight: 500 }}>You're offline. Updates will sync when reconnected.</span>
        </div>
      )}

      {/* Sticky top banner: centred wordmark opening the league switcher,
          settings on the right. */}
      <HomeNavBar demo={demo} />

      {/* Full bleed: outside the padded container so the band reaches both
          screen edges, carrying the page's own max width and gutters
          internally so its content still lines up with everything below.
          Once the countdowns run out nothing takes its place: the week is on
          each league card and over the week's scores. */}
      <SeasonCountdown />

      {/* Page content — max width 600px, centered */}
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '24px 16px' }}>

        {/* Release notes are for returning members, not a first look. */}
        {!demo && <WhatsNewCard />}

        {/* Greeting. The subtitle is for the demo and for someone with no
            league yet; with leagues, the Your Leagues label below already
            says what comes next, so a second heading only repeated it. */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 24, fontWeight: 900, color: 'var(--color-text-strong)', margin: 0 }}>
            {demo ? "Welcome to the demo" : `Welcome back, ${userName}`}
          </h1>
          {(demo || (!leaguesLoading && leagueList.length === 0)) && (
            <p style={{ fontSize: 14, color: 'var(--color-text-muted)', marginTop: 4 }}>
              {demo ? "You're managing Demo Squad. Open the league to look around." : "Get Started by Creating or Joining a League"}
            </p>
          )}
        </div>

        {/* Flash message — optional green banner passed via router state (e.g. after creating a league) */}
        {flashMessage && (
          <div style={{ backgroundColor: 'var(--color-success-bg)', border: '1px solid var(--color-success-border)', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
            <p style={{ fontSize: 14, color: 'var(--color-success-deep)', margin: 0 }}>{flashMessage}</p>
          </div>
        )}

        {leagueList.length === 0 ? (
          /* Empty state — user has no leagues yet. Show prominent create + join CTAs */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div
              onClick={() => navigate("/create-league")}
              style={{ backgroundColor: 'var(--color-brand)', borderRadius: 16, padding: '24px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16 }}
            >
              <div style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Trophy size={24} color="var(--color-bg-card)" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>Create a league</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>Start your own and invite friends</div>
              </div>
              <ArrowRight size={20} color="rgba(255,255,255,0.7)" />
            </div>

            <div
              onClick={() => navigate("/join-league")}
              style={{ backgroundColor: 'var(--color-bg-card)', border: '1.5px solid var(--color-border)', borderRadius: 16, padding: '24px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16 }}
            >
              <div style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: 'var(--color-band-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Users size={24} color="var(--color-brand)" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)' }}>Join a league</div>
                <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 2 }}>Got an invite code? Join now</div>
              </div>
              <ArrowRight size={20} color="var(--color-text-faint)" />
            </div>
          </div>
        ) : (
          /* League list — user has at least one league. Each card navigates to that league's my-lineup page */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <h2 style={SECTION_HEADING}>
                Your Leagues
                {/* Only once a league is scoring: before that the card has no
                    news bar, table or lineup to explain. */}
                {leagueList.some((l) => l.card) && (
                  <HelpMark steps={LEAGUE_CARD_STEPS} label="How the league card works" demo={demo} />
                )}
              </h2>
              {/* Green dot indicates live network connection */}
              {isOnline && <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: 'var(--color-brand-green)', display: 'inline-block' }} title="Live" />}
            </div>

            {/* One card per league, side by side. A second league is a swipe
                away rather than a scroll down, and the next card peeks in from
                the right so it is clear there is one. A single league takes
                the full width. */}
            <LeagueRail leagues={leagueList} />

            {/* Secondary create/join buttons — for users who want to add another league.
                Not in the demo, where the DemoBar's Sign up is the way in. */}
            {!demo && <div data-tour="home-join" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 4 }}>
              <button
                onClick={() => navigate("/create-league")}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '12px 0', borderRadius: 12, border: '1.5px dashed var(--color-border)', backgroundColor: 'var(--color-bg-card)', color: 'var(--color-text-muted)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
              >
                <Plus size={15} /> Create league
              </button>
              <button
                onClick={() => navigate("/join-league")}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '12px 0', borderRadius: 12, border: '1.5px dashed var(--color-border)', backgroundColor: 'var(--color-bg-card)', color: 'var(--color-text-muted)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
              >
                <Users size={15} /> Join league
              </button>
            </div>}
          </div>
        )}

        {/* Last week's best and worst, straight under the leagues: it is the
            league story most worth a returning manager's first look, and it
            sat unseen below the news. Renders nothing until a week has been
            scored. */}
        <WeeklyAwardsCard />

        <GamesSection demo={demo} />

        <WeeklyGameSection demo={demo} />

        {/* The week's football comes before the news: on a Saturday a live
            score outranks a headline, and midweek it is the fixtures a manager
            is planning around. */}
        <div style={{ marginTop: 32 }} data-tour="home-games">
          <ThisWeekBanner />
        </div>

        <div style={{ marginTop: 32 }} data-tour="home-news">
          <CfbNewsBanner />
        </div>

        <TopTeamsCard />
      </div>
      {demo && <DemoBar aboveTabBar={false} />}
    </div>
  );
}

export default Home;
